import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AvatarComponent } from '../avatar/avatar.component';
import { User } from '../../entities';
import { NamePipe } from '../../pipes/name.pipe';
import { UserService } from '../../../services/user.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'at-display-name',
  standalone: true,
  templateUrl: './display-name.component.html',
  styleUrl: './display-name.component.scss',
  imports: [AvatarComponent, NamePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.with-avatar]': 'showAvatar()',
    '[class.with-email]': 'shouldShowEmail()',
    '[class.is-me]': 'isMe()',
    '[class.no-name]': '!hasName() && !replaceNameWith()',
    '[class]': 'size()',
  },
})
export class DisplayNameComponent {
  private readonly userService = inject(UserService);

  private readonly loggedInUser = toSignal(this.userService.user$);

  public readonly user = input<User | null | undefined>(null);
  public readonly showEmail = input(false, { transform: booleanAttribute });
  public readonly showAvatar = input(false, { transform: booleanAttribute });
  public readonly showOwnerBadge = input(false, { transform: booleanAttribute });
  public readonly hideMeBadge = input(false, { transform: booleanAttribute });
  public readonly size = input<'xs' | 'sm' | 'md' | 'lg'>('md');
  public readonly replaceNameWith = input<'email' | 'unknown' | null>('email');

  protected readonly hasName = computed(() => {
    const user = this.user();
    return user && user.name?.trim();
  });

  protected readonly isNameReplaced = computed(() => {
    return !this.hasName() && this.replaceNameWith() !== null;
  });

  protected readonly shouldShowEmail = computed(() => {
    return (
      (this.showEmail() && this.replaceNameWith() !== 'email') ||
      (this.showEmail() && this.replaceNameWith() === 'email' && this.hasName())
    );
  });

  protected readonly isMe = computed(() => {
    const user = this.user();
    const loggedInUser = this.loggedInUser();

    return user && loggedInUser && user.email === loggedInUser.email;
  });
}
